import React, { useMemo } from 'react';
import {
  DataGrid,
  GridColDef,
  CellTypes,
  Button,
  Icon,
  DataGridProps,
} from '@fashionnovateam/nova-ui-toolkit';
import { find, get } from 'lodash';
import { Link } from 'react-router-dom';
import { Factory } from '../../Lib/Api/factory';
import makeFactoryStatus from '../../Lib/Utils/Factory/make-factory-status';
import CountryList from '../../Lib/Constants/countries';

type FactoryRow = {
  id: number;
  legal_name: CellTypes.DataGridCellBadgeParams;
  status: CellTypes.DataGridCellBadgeParams;
  contact_name: CellTypes.DataGridCellPersonParams;
  contact_email: CellTypes.DataGridCellEmailParams;
  contact_phone: CellTypes.DataGridCellStringParams;
  country: CellTypes.DataGridCellStringParams;
  audit_expiration_date: CellTypes.DataGridCellDateTimeParams;
  view: CellTypes.DataGridCellCustomParams;
};

const columns: GridColDef[] = [
  {
    field: 'legal_name',
    headerName: 'Name',
    render: 'badge',
    flex: 1,
    sortable: true,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'status',
    headerName: 'Status',
    render: 'badge',
    flex: 1,
    sortable: false,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'contact_name',
    headerName: 'Contact Name',
    render: 'person',
    flex: 1,
    sortable: true,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'contact_email',
    headerName: 'Contact Email',
    render: 'email',
    flex: 1,
    sortable: false,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'contact_phone',
    headerName: 'Contact Phone',
    render: 'string',
    flex: 1,
    sortable: false,
    minWidth: 150,
    display: 'flex',
    disableColumnMenu: true,
  },
  {
    field: 'country',
    headerName: 'Country',
    render: 'string',
    flex: 1,
    sortable: false,
    minWidth: 150,
    display: 'flex',
    disableColumnMenu: true,
  },
  {
    field: 'audit_expiration_date',
    headerName: 'Audit Expiration',
    render: 'dateTime',
    flex: 1,
    sortable: true,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'view',
    headerName: '',
    render: 'custom',
    flex: 1,
    sortable: false,
    minWidth: 150,
    disableColumnMenu: true,
  },
];

type Props = {
  userId: string;
  rows: Factory[];
  count: number;
  loading: boolean;
  params: { sortBy: string; sortOrder: 'ASC' | 'DESC'; page: number };
} & Omit<DataGridProps<FactoryRow>, 'columns'>;

function FactoryDataGrid({
  userId,
  rows,
  count,
  loading,
  params,
  ...datagridProps
}: Props) {
  const factoryStatusColorMapping: {
    [key: string]: 'green' | 'yellow' | 'red';
  } = {
    Approved: 'green',
    Rejected: 'red',
    'Pending Review': 'yellow',
  };

  const transformedRows = useMemo(
    () =>
      rows.map((factory: Factory) => {
        const status = makeFactoryStatus(factory);
        return {
          id: factory.id,
          legal_name: {
            children: factory.legal_name,
            contrast: 'low',
          },
          status: {
            children: status,
            contrast: 'low',
            color: factoryStatusColorMapping[status],
          },
          contact_name: {
            name: factory.contact_name,
          },
          contact_email: factory.contact_email,
          contact_phone: factory.contact_phone,
          audit_expiration_date: {
            activity: false,
            date: factory.audit_expiration_date,
            time: false,
          },
          country:
            get(
              find(CountryList, ({ value }) => value === factory.country),
              'text',
            ) || factory.country,
          view: (
            <Link to={`/users/${userId}/factories/${factory.id}`}>
              <Button size="sm" color="secondary">
                <Icon name="external-link/external-link-circle" />
                View
              </Button>
            </Link>
          ),
        } satisfies FactoryRow;
      }),
    [rows],
  );

  return (
    <DataGrid
      stateName="compliance_datagrid"
      hideFilterButton
      hideGroupButton
      columns={columns}
      loading={loading}
      rows={transformedRows}
      initialState={{
        pinnedColumns: {
          left: ['legal_name'],
        },
      }}
      sortingMode="server"
      paginationMode="server"
      rowCount={count || 0}
      pageSizeOptions={[15]}
      {...datagridProps}
    />
  );
}

export default FactoryDataGrid;
